import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import { apiCall, API_URL } from '../utils/api';

const AdminBannerManagement = () => {
    const [banners, setBanners] = useState({});
    const [selectedPage, setSelectedPage] = useState('home');
    const [showForm, setShowForm] = useState(false);

    const pages = [
        { id: 'home', name: 'Home Page' },
        { id: 'classes', name: 'Classes Page' },
        { id: 'booking', name: 'Booking Page' },
        { id: 'gallery', name: 'Gallery Page' },
        { id: 'about', name: 'About Us Page' },
        { id: 'become-teacher', name: 'Become a Teacher Page' }
    ];

    const [bannerForm, setBannerForm] = useState({
        imageUrl: '',
        heading: '',
        subheading: '',
        enabled: true,
        headingColor: '#ffffff',
        subheadingColor: '#ffffff',
        headingSize: '48px',
        subheadingSize: '24px'
    });

    // Load banners from API
    useEffect(() => {
        const fetchBanners = async () => {
            const newBanners = {};
            for (const page of pages) {
                try {
                    const data = await apiCall(`/banners/${page.id}`);
                    if (data) {
                        newBanners[page.id] = {
                            imageUrl: data.image ? (data.image.startsWith('http') ? data.image : `${API_URL.replace('/api', '')}/${data.image}`) : '',
                            heading: data.title || '',
                            subheading: data.subtitle || '',
                            enabled: data.enabled !== undefined ? data.enabled : true,
                            headingColor: '#ffffff', // Defaults
                            subheadingColor: '#ffffff',
                            headingSize: '48px',
                            subheadingSize: '24px'
                        };
                    }
                } catch (error) {
                    // Ignore 404s (banner not set yet)
                }
            }
            setBanners(newBanners);
        };
        fetchBanners();
    }, []);

    // Load current page banner into form
    useEffect(() => {
        if (banners[selectedPage]) {
            setBannerForm(banners[selectedPage]);
        }
    }, [selectedPage, banners]);

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setBannerForm({ ...bannerForm, imageUrl: reader.result, imageFile: file });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSaveBanner = async () => {
        if (!bannerForm.imageUrl && !bannerForm.imageFile) {
            alert('Please upload a banner image');
            return;
        }

        const formData = new FormData();
        formData.append('title', bannerForm.heading);
        formData.append('subtitle', bannerForm.subheading);
        formData.append('enabled', bannerForm.enabled); // Send enabled status

        if (bannerForm.imageFile) {
            formData.append('image', bannerForm.imageFile);
        }

        try {
            const savedBanner = await apiCall(`/banners/${selectedPage}`, 'PUT', formData, true);

            const updatedBanners = {
                ...banners,
                [selectedPage]: {
                    ...bannerForm,
                    imageUrl: savedBanner.image ? (savedBanner.image.startsWith('http') ? savedBanner.image : `${API_URL.replace('/api', '')}/${savedBanner.image}`) : bannerForm.imageUrl,
                    enabled: savedBanner.enabled
                }
            };

            setBanners(updatedBanners);
            setShowForm(false);
            alert('Banner saved successfully!');
        } catch (error) {
            alert('Failed to save banner: ' + error.message);
        }
    };

    const handleToggleStatus = async () => {
        const newStatus = !bannerForm.enabled;
        const updatedForm = { ...bannerForm, enabled: newStatus };

        // Optimistic update
        setBannerForm(updatedForm);
        setBanners({
            ...banners,
            [selectedPage]: updatedForm
        });

        // Call API
        const formData = new FormData();
        formData.append('enabled', newStatus);
        // We might want to send other fields too if PUT replaces everything, 
        // but with findOneAndUpdate + $set it should be partial. 
        // However, our controller updates title/subtitle too. 
        // Best to send current title/subtitle to be safe, or check controller logic.
        // Controller uses req.body values. If they are undefined, they might overwrite?
        // Let's check controller: 
        // const { title, subtitle, enabled } = req.body;
        // let updates = { title, subtitle, ... }; 
        // If title is undefined, updates.title is undefined. Mongo $set will ignore undefined? 
        // No, in JS { title: undefined } is an object with title: undefined. 
        // Mongoose might store null if not handled.
        // Let's send everything to be safe.

        formData.append('title', bannerForm.heading || '');
        formData.append('subtitle', bannerForm.subheading || '');

        try {
            await apiCall(`/banners/${selectedPage}`, 'PUT', formData, true);
        } catch (error) {
            alert('Failed to update status: ' + error.message);
            // Revert on error
            setBannerForm({ ...bannerForm, enabled: !newStatus });
            setBanners({
                ...banners,
                [selectedPage]: { ...bannerForm, enabled: !newStatus }
            });
        }
    };

    const handleDeleteBanner = () => {
        if (window.confirm('Are you sure you want to delete this banner?')) {
            const updatedForm = {
                imageUrl: '',
                heading: '',
                subheading: '',
                enabled: true,
                headingColor: '#ffffff',
                subheadingColor: '#ffffff',
                headingSize: '48px',
                subheadingSize: '24px'
            };

            setBannerForm(updatedForm);

            const updatedBanners = {
                ...banners,
                [selectedPage]: updatedForm
            };

            setBanners(updatedBanners);
            localStorage.setItem('pageBanners', JSON.stringify(updatedBanners));
            alert('Banner deleted successfully!');
        }
    };

    const currentBanner = banners[selectedPage] || bannerForm;

    return (
        <div className="admin-banner-management">
            <div className="section-header">
                <h2>🎨 Banner Management</h2>
                <p>Manage banners for all pages with full control</p>
            </div>

            {/* Page Selector */}
            <div className="card" style={{ marginBottom: '30px' }}>
                <div className="card-header">
                    <h3>Select Page</h3>
                </div>
                <div className="card-content">
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                        {pages.map(page => (
                            <button
                                key={page.id}
                                onClick={() => setSelectedPage(page.id)}
                                className={`page-selector-btn ${selectedPage === page.id ? 'active' : ''}`}
                                style={{
                                    padding: '15px',
                                    borderRadius: '8px',
                                    border: selectedPage === page.id ? '2px solid #4CAF50' : '2px solid #ddd',
                                    background: selectedPage === page.id ? '#e8f5e9' : '#fff',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>{page.name}</div>
                                <div style={{ fontSize: '12px', color: '#666' }}>
                                    {banners[page.id]?.enabled ? '✓ Active' : '✗ Inactive'}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Banner Editor */}
            <div className="card">
                <div className="card-header">
                    <h3>Edit Banner - {pages.find(p => p.id === selectedPage)?.name}</h3>
                    <div>
                        <button
                            onClick={handleToggleStatus}
                            className={`btn ${currentBanner.enabled ? 'btn-warning' : 'btn-success'}`}
                            style={{ marginRight: '10px' }}
                        >
                            {currentBanner.enabled ? '🔴 Disable' : '🟢 Enable'}
                        </button>
                        <button onClick={() => setShowForm(!showForm)} className="btn btn-primary">
                            {showForm ? 'Hide Editor' : 'Show Editor'}
                        </button>
                    </div>
                </div>
                <div className="card-content">
                    {/* Current Banner Preview */}
                    <div style={{ marginBottom: '30px' }}>
                        <h4>Current Banner Preview</h4>
                        {currentBanner.imageUrl ? (
                            <div style={{
                                position: 'relative',
                                width: '100%',
                                height: '400px',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                            }}>
                                <img
                                    src={currentBanner.imageUrl}
                                    alt="Banner Preview"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                    }}
                                />
                                <div style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    textAlign: 'center',
                                    width: '90%'
                                }}>
                                    {currentBanner.heading && (
                                        <h1 style={{
                                            color: currentBanner.headingColor,
                                            fontSize: currentBanner.headingSize,
                                            textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                                            marginBottom: '10px'
                                        }}>
                                            {currentBanner.heading}
                                        </h1>
                                    )}
                                    {currentBanner.subheading && (
                                        <p style={{
                                            color: currentBanner.subheadingColor,
                                            fontSize: currentBanner.subheadingSize,
                                            textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                                        }}>
                                            {currentBanner.subheading}
                                        </p>
                                    )}
                                </div>
                                {!currentBanner.enabled && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '10px',
                                        right: '10px',
                                        background: '#dc3545',
                                        color: 'white',
                                        padding: '8px 16px',
                                        borderRadius: '4px',
                                        fontWeight: 'bold'
                                    }}>
                                        DISABLED
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div style={{
                                padding: '60px',
                                textAlign: 'center',
                                background: '#f8f9fa',
                                borderRadius: '8px',
                                border: '2px dashed #ddd'
                            }}>
                                <p style={{ color: '#666' }}>No banner set for this page. Click "Show Editor" to create one.</p>
                            </div>
                        )}
                    </div>

                    {/* Banner Form */}
                    {showForm && (
                        <div className="form-card" style={{ padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
                            <h4>Banner Settings</h4>
                            <div className="form-grid">
                                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                    <label>Upload Banner Image *</label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                    />
                                    <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                                        Recommended size: 1343x324px (or 1920x600px for full HD)
                                    </small>
                                </div>

                                <div className="form-group">
                                    <label>Banner Heading</label>
                                    <input
                                        type="text"
                                        value={bannerForm.heading}
                                        onChange={(e) => setBannerForm({ ...bannerForm, heading: e.target.value })}
                                        placeholder="Enter banner heading"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Banner Subheading</label>
                                    <input
                                        type="text"
                                        value={bannerForm.subheading}
                                        onChange={(e) => setBannerForm({ ...bannerForm, subheading: e.target.value })}
                                        placeholder="Enter banner subheading"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Heading Color</label>
                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                        <input
                                            type="color"
                                            value={bannerForm.headingColor}
                                            onChange={(e) => setBannerForm({ ...bannerForm, headingColor: e.target.value })}
                                            style={{ width: '60px', height: '40px', cursor: 'pointer' }}
                                        />
                                        <input
                                            type="text"
                                            value={bannerForm.headingColor}
                                            onChange={(e) => setBannerForm({ ...bannerForm, headingColor: e.target.value })}
                                            placeholder="#ffffff"
                                            style={{ flex: 1 }}
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Subheading Color</label>
                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                        <input
                                            type="color"
                                            value={bannerForm.subheadingColor}
                                            onChange={(e) => setBannerForm({ ...bannerForm, subheadingColor: e.target.value })}
                                            style={{ width: '60px', height: '40px', cursor: 'pointer' }}
                                        />
                                        <input
                                            type="text"
                                            value={bannerForm.subheadingColor}
                                            onChange={(e) => setBannerForm({ ...bannerForm, subheadingColor: e.target.value })}
                                            placeholder="#ffffff"
                                            style={{ flex: 1 }}
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Heading Font Size</label>
                                    <select
                                        value={bannerForm.headingSize}
                                        onChange={(e) => setBannerForm({ ...bannerForm, headingSize: e.target.value })}
                                    >
                                        <option value="32px">Small (32px)</option>
                                        <option value="40px">Medium (40px)</option>
                                        <option value="48px">Large (48px)</option>
                                        <option value="56px">Extra Large (56px)</option>
                                        <option value="64px">Huge (64px)</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Subheading Font Size</label>
                                    <select
                                        value={bannerForm.subheadingSize}
                                        onChange={(e) => setBannerForm({ ...bannerForm, subheadingSize: e.target.value })}
                                    >
                                        <option value="16px">Small (16px)</option>
                                        <option value="20px">Medium (20px)</option>
                                        <option value="24px">Large (24px)</option>
                                        <option value="28px">Extra Large (28px)</option>
                                    </select>
                                </div>
                            </div>

                            <div className="form-actions" style={{ marginTop: '20px' }}>
                                <button onClick={handleSaveBanner} className="btn btn-success">💾 Save Banner</button>
                                <button onClick={handleDeleteBanner} className="btn btn-danger">🗑️ Delete Banner</button>
                                <button onClick={() => setShowForm(false)} className="btn btn-secondary">Cancel</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Quick Stats */}
            <div className="card" style={{ marginTop: '30px' }}>
                <div className="card-header">
                    <h3>Banner Status Overview</h3>
                </div>
                <div className="card-content">
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px' }}>
                        {pages.map(page => {
                            const banner = banners[page.id];
                            const hasImage = banner?.imageUrl;
                            const isEnabled = banner?.enabled;

                            return (
                                <div
                                    key={page.id}
                                    style={{
                                        padding: '15px',
                                        borderRadius: '8px',
                                        background: hasImage && isEnabled ? '#e8f5e9' : '#ffebee',
                                        border: `2px solid ${hasImage && isEnabled ? '#4CAF50' : '#f44336'}`,
                                        textAlign: 'center'
                                    }}
                                >
                                    <div style={{ fontSize: '24px', marginBottom: '5px' }}>
                                        {hasImage && isEnabled ? '✅' : '❌'}
                                    </div>
                                    <div style={{ fontSize: '12px', fontWeight: 'bold' }}>{page.name}</div>
                                    <div style={{ fontSize: '10px', color: '#666', marginTop: '5px' }}>
                                        {hasImage ? (isEnabled ? 'Active' : 'Disabled') : 'No Banner'}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminBannerManagement;
